import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_SERVICE_BUNDLES } from "../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import { getProfessionals, Professional } from "../../../../services/professionalService";
import { getServices, Service } from "../../../../services/serviceService";
import {
  createServiceBundle,
  deleteServiceBundle,
  getServiceBundle,
  getServiceBundles,
  ServiceBundle,
  toggleServiceBundleStatus,
  updateServiceBundle,
} from "../../../../services/serviceBundleService";
import { getUnits, Unit } from "../../../../services/unitService";
import { PaginationMeta } from "../../../../types/api";
import {
  CreateServiceBundlePayload,
  ServiceBundlePricingMode,
  ServiceBundleScheduleType,
} from "../../../../types/serviceBundle";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const CATALOG_PAGE_SIZE = 100;

interface BundleItemFormValues {
  rowId: string;
  id?: number;
  service_id: string;
  gap_minutes: number;
  professional_id: string;
}

interface ServiceBundleFormValues {
  unit_id: string;
  name: string;
  description: string;
  pricing_mode: ServiceBundlePricingMode;
  price_display: string;
  discount_percent: string;
  schedule_type: ServiceBundleScheduleType;
  show_in_online_booking: boolean;
  is_active: boolean;
  items: BundleItemFormValues[];
}

const newItemRow = (): BundleItemFormValues => ({
  rowId: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  service_id: "",
  gap_minutes: 0,
  professional_id: "",
});

const emptyFormValues = (): ServiceBundleFormValues => ({
  unit_id: "",
  name: "",
  description: "",
  pricing_mode: "sum",
  price_display: "",
  discount_percent: "",
  schedule_type: "sequential",
  show_in_online_booking: true,
  is_active: true,
  items: [newItemRow(), newItemRow()],
});

const mapBundleToFormValues = (bundle: ServiceBundle): ServiceBundleFormValues => ({
  unit_id: String(bundle.unit_id),
  name: bundle.name,
  description: bundle.description ?? "",
  pricing_mode: bundle.pricing_mode,
  price_display: bundle.price !== null ? formatCentsToMoneyInput(bundle.price) : "",
  discount_percent:
    bundle.discount_bps !== null ? String(bundle.discount_bps / 100) : "",
  schedule_type: bundle.schedule_type,
  show_in_online_booking: bundle.show_in_online_booking,
  is_active: bundle.is_active,
  items: (bundle.items ?? [])
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item) => ({
      rowId: `item-${item.id}`,
      id: item.id,
      service_id: String(item.service_id),
      gap_minutes: item.gap_minutes,
      professional_id: item.professional_id ? String(item.professional_id) : "",
    })),
});

const buildBundlePayload = (values: ServiceBundleFormValues): CreateServiceBundlePayload => ({
  unit_id: Number(values.unit_id),
  name: values.name.trim(),
  description: values.description.trim() || null,
  schedule_type: values.schedule_type,
  pricing_mode: values.pricing_mode,
  price:
    values.pricing_mode === "fixed"
      ? parseMoneyInputToCents(values.price_display)
      : null,
  discount_bps:
    values.pricing_mode === "discount_percent"
      ? Math.round(Number.parseFloat(values.discount_percent || "0") * 100)
      : null,
  show_in_online_booking: values.show_in_online_booking,
  is_active: values.is_active,
  items: values.items.map((item) => ({
    ...(item.id ? { id: item.id } : {}),
    service_id: Number(item.service_id),
    gap_minutes:
      values.schedule_type === "sequential" ? Number(item.gap_minutes) || 0 : 0,
    professional_id: item.professional_id ? Number(item.professional_id) : null,
  })),
});

const ServiceBundlesList = () => {
  const { t, i18n } = useTranslation();

  document.title = t("studio.bundles.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const [bundles, setBundles] = useState<ServiceBundle[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<ServiceBundle | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceBundle | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const [unitServices, setUnitServices] = useState<Service[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [formResourcesLoading, setFormResourcesLoading] = useState(false);

  const notAvailable = t("studio.bundles.common.notAvailable");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.bundles.validation.unitRequired")),
        name: Yup.string().trim().required(t("studio.bundles.validation.nameRequired")),
        description: Yup.string().nullable(),
        pricing_mode: Yup.string()
          .oneOf(["sum", "fixed", "discount_percent"])
          .required(),
        price_display: Yup.string().when("pricing_mode", {
          is: "fixed",
          then: (schema) =>
            schema
              .required(t("studio.bundles.validation.priceRequired"))
              .test(
                "price-valid",
                t("studio.bundles.validation.priceInvalid"),
                (value) => parseMoneyInputToCents(value || "") > 0
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        discount_percent: Yup.string().when("pricing_mode", {
          is: "discount_percent",
          then: (schema) =>
            schema
              .required(t("studio.bundles.validation.discountRequired"))
              .test(
                "discount-range",
                t("studio.bundles.validation.discountInvalid"),
                (value) => {
                  const parsed = Number.parseFloat(value || "");
                  return !Number.isNaN(parsed) && parsed > 0 && parsed <= 100;
                }
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        schedule_type: Yup.string().oneOf(["sequential", "parallel"]).required(),
        items: Yup.array()
          .of(
            Yup.object({
              service_id: Yup.string().required(t("studio.bundles.validation.serviceRequired")),
              gap_minutes: Yup.number()
                .typeError(t("studio.bundles.validation.gapType"))
                .min(0, t("studio.bundles.validation.gapMin")),
              professional_id: Yup.string().nullable(),
            })
          )
          .min(2, t("studio.bundles.validation.itemsMin"))
          .max(10, t("studio.bundles.validation.itemsMax"))
          .test(
            "unique-services",
            t("studio.bundles.validation.serviceDuplicate"),
            (items) => {
              if (!items) {
                return true;
              }
              const ids = items
                .map((item) => item?.service_id)
                .filter((id): id is string => Boolean(id));
              return new Set(ids).size === ids.length;
            }
          ),
      }),
    [t]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, unitFilter]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: CATALOG_PAGE_SIZE });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.bundles.toast.loadError"));
    }
  }, [t]);

  const fetchBundles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getServiceBundles({
        page,
        per_page: PER_PAGE,
        ...(unitFilter ? { unit_id: Number(unitFilter) } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setBundles(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.bundles.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, unitFilter, t]);

  const loadFormResources = useCallback(
    async (unitId: string) => {
      if (!unitId) {
        setUnitServices([]);
        return;
      }

      setFormResourcesLoading(true);
      try {
        const [servicesResponse, professionalsResponse] = await Promise.all([
          getServices({ unit_id: Number(unitId), per_page: CATALOG_PAGE_SIZE }),
          getProfessionals({ per_page: CATALOG_PAGE_SIZE }),
        ]);
        setUnitServices(servicesResponse.data ?? []);
        setProfessionals(professionalsResponse.data ?? []);
      } catch (error: any) {
        toast.error(error?.data || t("studio.bundles.toast.loadError"));
      } finally {
        setFormResourcesLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    fetchBundles();
  }, [fetchBundles]);

  const formInitialValues = useMemo(
    () =>
      editingBundle
        ? mapBundleToFormValues(editingBundle)
        : { ...emptyFormValues(), unit_id: unitFilter || "" },
    [editingBundle, unitFilter]
  );

  const formik = useFormik<ServiceBundleFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildBundlePayload(values);

      try {
        if (editingBundle) {
          const response = await updateServiceBundle(editingBundle.id, payload);
          toast.success(response.message);
        } else {
          const response = await createServiceBundle(payload);
          toast.success(response.message);
        }
        closeForm();
        if (!editingBundle && page !== 1) {
          setPage(1);
        } else {
          await fetchBundles();
        }
      } catch (error: any) {
        toast.error(error?.data || t("studio.bundles.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (formOpen && formik.values.unit_id) {
      loadFormResources(formik.values.unit_id);
    }
  }, [formOpen, formik.values.unit_id, loadFormResources]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingBundle(null);
    setUnitServices([]);
    setFormLoading(false);
  };

  const openCreate = () => {
    setEditingBundle(null);
    setFormLoading(false);
    setFormOpen(true);
  };

  const openEdit = async (bundle: ServiceBundle) => {
    setEditingBundle(null);
    setFormLoading(true);
    setFormOpen(true);

    try {
      const response = await getServiceBundle(bundle.id);
      setEditingBundle(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.bundles.toast.loadError"));
      closeForm();
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await deleteServiceBundle(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      const isLastOnPage = bundles.length === 1 && page > 1;
      if (isLastOnPage) {
        setPage(page - 1);
      } else {
        await fetchBundles();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.bundles.toast.deleteError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (bundle: ServiceBundle) => {
    setSubmitting(true);
    try {
      const response = await toggleServiceBundleStatus(bundle.id);
      toast.success(response.message);
      await fetchBundles();
    } catch (error: any) {
      toast.error(error?.data || t("studio.bundles.toast.toggleError"));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveUnitName = (unitId: number): string =>
    units.find((unit) => unit.id === unitId)?.name ?? notAvailable;

  const pricingModeLabel = (mode: ServiceBundlePricingMode): string =>
    t(`studio.bundles.pricingMode.${mode}`);

  const scheduleTypeLabel = (type: ServiceBundleScheduleType): string =>
    t(`studio.bundles.scheduleType.${type}`);

  const isSequential = formik.values.schedule_type === "sequential";

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.bundles.breadcrumb.title")}
          pageTitle={t("studio.bundles.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">{t("studio.bundles.card.title")}</h4>
                <HelpArticleLink articlePath={HELP_ARTICLE_SERVICE_BUNDLES} className="align-self-center" />
                <Button color="primary" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  {t("studio.bundles.actions.new")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Input
                      type="select"
                      value={unitFilter}
                      onChange={(event) => setUnitFilter(event.target.value)}
                      aria-label={t("studio.bundles.filter.unit")}
                    >
                      <option value="">{t("studio.bundles.filter.allUnits")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={8}>
                    <Input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={t("studio.bundles.search.placeholder")}
                    />
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">{t("studio.bundles.loading")}</Spinner>
                  </div>
                ) : bundles.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {debouncedSearch
                        ? t("studio.bundles.empty.noResults", { term: debouncedSearch })
                        : t("studio.bundles.empty.noData")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("studio.bundles.actions.createFirst")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.bundles.table.name")}</th>
                            <th>{t("studio.bundles.table.unit")}</th>
                            <th>{t("studio.bundles.table.schedule")}</th>
                            <th>{t("studio.bundles.table.pricing")}</th>
                            <th>{t("studio.bundles.table.duration")}</th>
                            <th>{t("studio.bundles.table.price")}</th>
                            <th>{t("studio.bundles.table.status")}</th>
                            <th className="text-end">{t("studio.bundles.table.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bundles.map((bundle) => (
                            <tr key={bundle.id}>
                              <td>{bundle.name}</td>
                              <td>{resolveUnitName(bundle.unit_id)}</td>
                              <td>{scheduleTypeLabel(bundle.schedule_type)}</td>
                              <td>{pricingModeLabel(bundle.pricing_mode)}</td>
                              <td>
                                {t("studio.bundles.durationValue", {
                                  minutes: bundle.duration_minutes,
                                })}
                              </td>
                              <td>
                                {bundle.price_is_from
                                  ? t("studio.services.price.from", {
                                      price: formatMoney(bundle.effective_price_cents),
                                    })
                                  : formatMoney(bundle.effective_price_cents)}
                              </td>
                              <td>
                                <Badge color={bundle.is_active ? "success" : "secondary"}>
                                  {bundle.is_active
                                    ? t("studio.bundles.status.active")
                                    : t("studio.bundles.status.inactive")}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-warning"
                                  size="sm"
                                  className="me-1"
                                  title={t("studio.bundles.actions.toggleStatus")}
                                  onClick={() => handleToggleStatus(bundle)}
                                  disabled={submitting}
                                >
                                  <i className="ri-toggle-line" />
                                </Button>
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  title={t("studio.bundles.actions.edit")}
                                  onClick={() => openEdit(bundle)}
                                >
                                  <i className="ri-pencil-line" />
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  title={t("studio.bundles.actions.delete")}
                                  onClick={() => {
                                    setDeleteTarget(bundle);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 && (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.bundles.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t("studio.bundles.pagination.previous")}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={page >= meta.last_page}>
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < meta.last_page) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.bundles.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas isOpen={formOpen} toggle={closeForm} direction="end" className="w-50">
        <OffcanvasHeader toggle={closeForm}>
          {editingBundle
            ? t("studio.bundles.form.editTitle")
            : t("studio.bundles.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <FormikProvider value={formik}>
            <Form key={entityFormKey(editingBundle?.id)} onSubmit={formik.handleSubmit}>
              <div className="mb-3">
                <Label htmlFor="bundle-unit">{t("studio.bundles.fields.unit.label")}</Label>
                <Input
                  id="bundle-unit"
                  name="unit_id"
                  type="select"
                  value={formik.values.unit_id}
                  onChange={(event) => {
                    formik.handleChange(event);
                    formik.setFieldValue("items", [newItemRow(), newItemRow()]);
                  }}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.unit_id && formik.errors.unit_id)}
                >
                  <option value="">{t("studio.bundles.fields.unit.placeholder")}</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.unit_id}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label htmlFor="bundle-name">{t("studio.bundles.fields.name.label")}</Label>
                <Input
                  id="bundle-name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </div>

              <div className="mb-3">
                <Label htmlFor="bundle-description">
                  {t("studio.bundles.fields.description.label")}
                </Label>
                <Input
                  id="bundle-description"
                  name="description"
                  type="textarea"
                  rows={2}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </div>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="bundle-pricing-mode">
                      {t("studio.bundles.fields.pricingMode.label")}
                    </Label>
                    <Input
                      id="bundle-pricing-mode"
                      name="pricing_mode"
                      type="select"
                      value={formik.values.pricing_mode}
                      onChange={formik.handleChange}
                    >
                      <option value="sum">{t("studio.bundles.pricingMode.sum")}</option>
                      <option value="fixed">{t("studio.bundles.pricingMode.fixed")}</option>
                      <option value="discount_percent">
                        {t("studio.bundles.pricingMode.discount_percent")}
                      </option>
                    </Input>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="bundle-schedule-type">
                      {t("studio.bundles.fields.scheduleType.label")}
                    </Label>
                    <Input
                      id="bundle-schedule-type"
                      name="schedule_type"
                      type="select"
                      value={formik.values.schedule_type}
                      onChange={formik.handleChange}
                    >
                      <option value="sequential">
                        {t("studio.bundles.scheduleType.sequential")}
                      </option>
                      <option value="parallel">{t("studio.bundles.scheduleType.parallel")}</option>
                    </Input>
                  </div>
                </Col>
              </Row>

              {formik.values.pricing_mode === "fixed" ? (
                <div className="mb-3">
                  <Label htmlFor="bundle-price">{t("studio.bundles.fields.price.label")}</Label>
                  <Input
                    id="bundle-price"
                    name="price_display"
                    value={formik.values.price_display}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t("studio.bundles.fields.price.placeholder")}
                    invalid={
                      !!(formik.touched.price_display && formik.errors.price_display)
                    }
                  />
                  <FormFeedback>{formik.errors.price_display}</FormFeedback>
                </div>
              ) : null}

              {formik.values.pricing_mode === "discount_percent" ? (
                <div className="mb-3">
                  <Label htmlFor="bundle-discount">
                    {t("studio.bundles.fields.discount.label")}
                  </Label>
                  <Input
                    id="bundle-discount"
                    name="discount_percent"
                    type="number"
                    min={0.01}
                    max={100}
                    step={0.01}
                    value={formik.values.discount_percent}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t("studio.bundles.fields.discount.placeholder")}
                    invalid={
                      !!(formik.touched.discount_percent && formik.errors.discount_percent)
                    }
                  />
                  <FormFeedback>{formik.errors.discount_percent}</FormFeedback>
                </div>
              ) : null}

              <div className="mb-3 d-flex gap-4">
                <div className="form-check">
                  <Input
                    id="bundle-online-booking"
                    name="show_in_online_booking"
                    type="checkbox"
                    className="form-check-input"
                    checked={formik.values.show_in_online_booking}
                    onChange={formik.handleChange}
                  />
                  <Label htmlFor="bundle-online-booking" className="form-check-label">
                    {t("studio.bundles.fields.showInOnlineBooking")}
                  </Label>
                </div>
                <div className="form-check">
                  <Input
                    id="bundle-active"
                    name="is_active"
                    type="checkbox"
                    className="form-check-input"
                    checked={formik.values.is_active}
                    onChange={formik.handleChange}
                  />
                  <Label htmlFor="bundle-active" className="form-check-label">
                    {t("studio.bundles.fields.isActive")}
                  </Label>
                </div>
              </div>

              <hr />
              <h6 className="mb-3">{t("studio.bundles.items.title")}</h6>
              {typeof formik.errors.items === "string" ? (
                <div className="text-danger small mb-2">{formik.errors.items}</div>
              ) : null}

              {formResourcesLoading ? (
                <div className="text-center py-3">
                  <Spinner size="sm" color="primary" />
                </div>
              ) : (
                <FieldArray name="items">
                  {({ push, remove }) => (
                    <>
                      {formik.values.items.map((item, index) => {
                        const itemErrors = Array.isArray(formik.errors.items)
                          ? formik.errors.items[index]
                          : undefined;
                        const itemTouched = Array.isArray(formik.touched.items)
                          ? formik.touched.items[index]
                          : undefined;
                        const serviceIdError =
                          itemErrors &&
                          typeof itemErrors === "object" &&
                          !Array.isArray(itemErrors) &&
                          typeof (itemErrors as { service_id?: unknown }).service_id === "string"
                            ? ((itemErrors as { service_id: string }).service_id)
                            : undefined;
                        const serviceIdTouched =
                          itemTouched &&
                          typeof itemTouched === "object" &&
                          !Array.isArray(itemTouched) &&
                          (itemTouched as { service_id?: boolean }).service_id;

                        return (
                          <Card key={item.rowId} className="mb-3 border">
                            <CardBody className="py-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-medium">
                                  {t("studio.bundles.items.step", { index: index + 1 })}
                                </span>
                                {formik.values.items.length > 2 ? (
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    type="button"
                                    onClick={() => remove(index)}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                ) : null}
                              </div>
                              <div className="mb-2">
                                <Label>{t("studio.bundles.items.service")}</Label>
                                <Input
                                  name={`items.${index}.service_id`}
                                  type="select"
                                  value={item.service_id}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={!formik.values.unit_id}
                                  invalid={Boolean(serviceIdTouched && serviceIdError)}
                                >
                                  <option value="">
                                    {t("studio.bundles.items.servicePlaceholder")}
                                  </option>
                                  {unitServices.map((service) => (
                                    <option key={service.id} value={service.id}>
                                      {service.name} ({formatMoney(service.price)})
                                    </option>
                                  ))}
                                </Input>
                                {serviceIdError ? (
                                  <div className="invalid-feedback d-block">
                                    {serviceIdError}
                                  </div>
                                ) : null}
                              </div>
                              {isSequential ? (
                                <div className="mb-2">
                                  <Label>{t("studio.bundles.items.gap")}</Label>
                                  <Input
                                    name={`items.${index}.gap_minutes`}
                                    type="number"
                                    min={0}
                                    value={item.gap_minutes}
                                    onChange={formik.handleChange}
                                  />
                                </div>
                              ) : null}
                              <div>
                                <Label>{t("studio.bundles.items.professional")}</Label>
                                <Input
                                  name={`items.${index}.professional_id`}
                                  type="select"
                                  value={item.professional_id}
                                  onChange={formik.handleChange}
                                >
                                  <option value="">
                                    {t("studio.bundles.items.professionalPlaceholder")}
                                  </option>
                                  {professionals.map((professional) => (
                                    <option key={professional.id} value={professional.id}>
                                      {professional.name}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                            </CardBody>
                          </Card>
                        );
                      })}
                      {formik.values.items.length < 10 ? (
                        <Button
                          color="soft-primary"
                          type="button"
                          className="mb-3"
                          onClick={() => push(newItemRow())}
                          disabled={!formik.values.unit_id}
                        >
                          <i className="ri-add-line align-bottom me-1" />
                          {t("studio.bundles.items.add")}
                        </Button>
                      ) : null}
                    </>
                  )}
                </FieldArray>
              )}

              <div className="d-flex gap-2 justify-content-end">
                <Button color="light" type="button" onClick={closeForm}>
                  {t("studio.bundles.actions.cancel")}
                </Button>
                <Button color="primary" type="submit" disabled={submitting || formResourcesLoading}>
                  {submitting ? t("studio.bundles.loading") : t("studio.bundles.actions.save")}
                </Button>
              </div>
            </Form>
          </FormikProvider>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <h4>{t("studio.bundles.delete.title")}</h4>
            <p className="text-muted mx-4 mb-0">
              {deleteTarget
                ? t("studio.bundles.delete.description", { name: deleteTarget.name })
                : t("studio.bundles.delete.fallback")}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("studio.bundles.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={submitting}>
              {t("studio.bundles.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ServiceBundlesList;
